import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import DataGrid, { Filter } from '#/common/components/DataGrid';
import MatchItemsModal from '#/common/components/MatchItemsModal';
import type { Asset, GetAssetListQuery, GetAssetListQueryVariables } from '#/graphql/types';
import { gql, useQuery } from '@apollo/client';
import { getAssetList } from '#/graphql/queries';
import { createMatchAssetColumns } from './columns';
import { matchStatuses } from '../../utils/constants';
import { actions } from '../AssetDataGrid/actions';
import useToast from '#/common/hooks/useToast';
import MatchedAssetPreview from './MatchedAssetPreview';

const getAssetListQuery = gql(getAssetList);

export interface MatchAssetsModalProps {
  visible: boolean;
  asset?: Asset;
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm: (selectedTisAssetId: string | null) => Promise<void> | void;
}

const MatchAssetsModal: React.FC<MatchAssetsModalProps> = ({
  visible,
  asset,
  isSubmitting,
  onClose,
  onConfirm,
}) => {
  const { alert } = useToast();
  const selectedTisAssetIdRef = useRef<string | null>(null);
  const matchCandidatesGridRef = useRef<any>(null);

  const getSelectedAssetId = useCallback(() => selectedTisAssetIdRef.current, []);

  const isCurrentlyMatched =
    asset?.matchStatus === matchStatuses.matched.key && !!asset?.thirdPartyId;

  const { data: matchedTisAssetData } = useQuery<GetAssetListQuery, GetAssetListQueryVariables>(
    gql(getAssetList),
    {
      variables: {
        params: {
          filter: JSON.stringify([
            ['matchStatus', '=', matchStatuses.archived.key],
            'AND',
            ['thirdPartyId', '=', asset?.thirdPartyId],
            'AND',
            ['tenantId', '=', asset?.tenantId],
          ]),
          take: 1,
        },
      },
      skip: !isCurrentlyMatched,
    },
  );

  const matchedTisAsset = matchedTisAssetData?.getAssetList?.data?.[0] ?? null;

  useEffect(() => {
    selectedTisAssetIdRef.current = matchedTisAsset?.id ?? null;
  }, [matchedTisAsset?.id]);

  useEffect(() => {
    if (!visible) {
      selectedTisAssetIdRef.current = matchedTisAsset?.id ?? null;
    }
  }, [visible, matchedTisAsset?.id]);

  const handleSelectAssetId = useCallback((id: string) => {
    selectedTisAssetIdRef.current = id;

    // Repaint rows so custom radio cells re-evaluate checked state without reloading data.
    matchCandidatesGridRef.current?.instance?.repaint?.();
  }, []);

  const handleRowClick = useCallback(
    (e: any) => {
      const rowId = e?.data?.id;
      if (rowId) {
        handleSelectAssetId(rowId);
      }
    },
    [handleSelectAssetId],
  );

  const matchCandidatesPermanentFilters = useMemo(() => {
    const filters: Filter[] = [
      ['thirdPartyId', 'isNotNull'],
      'AND',
      ['source', 'isNotNull'],
      'AND',
      ['matchStatus', '=', matchStatuses.tis.key],
    ];

    if (asset?.id) filters.push('AND', ['id', '<>', asset.id]);

    if (asset?.location?.matchStatus === 'matched')
      filters.push(['AND', ['location.id', '=', asset.location.id]]);

    if (asset?.tenantId) filters.push('AND', ['tenantId', '=', asset.tenantId]);

    return filters;
  }, [asset?.id, asset?.location?.matchStatus, asset?.tenantId]);

  const matchCandidatesColumns = useMemo(
    () =>
      createMatchAssetColumns({
        getSelectedAssetId,
        onSelectAssetId: handleSelectAssetId,
      }),
    [getSelectedAssetId, handleSelectAssetId],
  );

  const handleConfirm = async () => {
    const selectedId = selectedTisAssetIdRef.current;

    if (!selectedId) {
      void alert('error', 'Please select an Asset to match');
      return;
    }

    await onConfirm(selectedId);
  };

  const handleClose = () => {
    selectedTisAssetIdRef.current = null;
    onClose();
  };

  const handleClearSelection = () => {
    if (!asset?.thirdPartyId) {
      void alert(
        'error',
        'The Asset is not currently matched with any TC Asset, so there is no selection to clear.',
      );
      return;
    }
    selectedTisAssetIdRef.current = null;
    void onConfirm(null);
  };

  return (
    <MatchItemsModal
      visible={visible}
      itemType="Asset"
      item={asset?.assetTag || asset?.displayNumber || asset?.id || '-'}
      isSubmitting={isSubmitting}
      warningMessage="All of the Asset's data will be updated after matching it with a new one."
      currentMatchPreview={
        matchedTisAsset ? <MatchedAssetPreview asset={matchedTisAsset} /> : undefined
      }
      onSecondaryAction={handleClearSelection}
      onClose={handleClose}
      onConfirm={handleConfirm}>
      <DataGrid
        id="match-assets-candidates"
        pluralText="Assets"
        singularText="Asset"
        selectionMode="none"
        columns={matchCandidatesColumns}
        getItemsQuery={getAssetListQuery}
        actions={actions}
        permanentFilters={matchCandidatesPermanentFilters}
        onRowClick={handleRowClick}
        disableStateStoring
        defaultPageSize={5}
        setRef={(ref) => {
          matchCandidatesGridRef.current = ref;
        }}
      />
    </MatchItemsModal>
  );
};

export default React.memo(MatchAssetsModal);
